/**
 * Service for minting short https://abmymix.com/s/:id links via a Netlify function.
 * Destinations are stored in a site-wide Netlify Blobs lookup table (not audio).
 *
 * Vite / Cloudflare tunnels do not run Netlify Functions, so we try the local
 * relative path first and then fall back to production.
 */

interface ShortenUrlResponse {
  shortUrl?: string;
  error?: string;
  details?: string;
}

export class UrlShorteningService {
  private static readonly LOCAL_FUNCTION_URL = '/.netlify/functions/shorten-url';
  private static readonly PRODUCTION_FUNCTION_URL = 'https://abmymix.com/.netlify/functions/shorten-url';

  /**
   * Preview hosts (Vite, Cloudflare tunnels, deploy previews) should mint
   * production short links so recipients hit abmymix.com, not an ephemeral URL.
   */
  static canonicalizeShareUrl(longUrl: string): string {
    try {
      const url = new URL(longUrl);
      const host = url.hostname.toLowerCase();
      const isPreview =
        host === 'localhost' ||
        host === '127.0.0.1' ||
        host === 'www.abmymix.com' ||
        host.endsWith('.trycloudflare.com') ||
        host.endsWith('.netlify.app');

      if (isPreview) {
        url.protocol = 'https:';
        url.hostname = 'abmymix.com';
        url.port = '';
      }
      return url.toString();
    } catch {
      return longUrl;
    }
  }

  private static functionUrls(): string[] {
    const urls = [this.LOCAL_FUNCTION_URL];
    if (typeof window !== 'undefined') {
      const host = window.location.hostname.toLowerCase();
      if (host !== 'abmymix.com' && host !== 'www.abmymix.com') {
        urls.push(this.PRODUCTION_FUNCTION_URL);
      }
    }
    return urls;
  }

  private static async postShorten(body: Record<string, string>): Promise<string> {
    const endpoints = this.functionUrls();
    let lastError: Error | null = null;

    for (let i = 0; i < endpoints.length; i++) {
      const endpoint = endpoints[i];
      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });

        if (response.status === 404 && i < endpoints.length - 1) {
          console.log('[UrlShorteningService] Function not on this host, trying production');
          continue;
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: ShortenUrlResponse = await response.json();
        if (data.shortUrl) {
          return data.shortUrl;
        }
        throw new Error(`API error: ${data.error || 'Unknown error'}${data.details ? ` - ${data.details}` : ''}`);
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        if (i < endpoints.length - 1) {
          continue;
        }
      }
    }

    throw lastError || new Error('URL shortening failed');
  }

  /**
   * Mints a short ABmyMix URL for the given destination.
   * @param longUrl The URL to shorten
   * @returns Promise<string> The short URL, or the original URL if minting fails
   */
  static async shortenUrl(longUrl: string): Promise<string> {
    const destination = this.canonicalizeShareUrl(longUrl);
    try {
      const shortUrl = await this.postShorten({
        url: destination,
        action: 'shorten',
      });
      console.log('[UrlShorteningService] Successfully shortened URL:', shortUrl);
      return shortUrl;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorDetails = error instanceof Error ? {
        message: error.message,
        stack: error.stack,
        name: error.name
      } : error;
      console.error('[UrlShorteningService] URL shortening failed:', {
        error: errorMessage,
        details: errorDetails,
        url: destination.substring(0, 100) + '...'
      });
      return longUrl;
    }
  }

  /**
   * Mints a new short URL for an updated destination.
   * Existing short IDs are not overwritten.
   * @param shortUrl The existing short URL (unused; kept for call-site compatibility)
   * @param newLongUrl The new URL to point to
   * @returns Promise<string> The new short URL or the new long URL if minting fails
   */
  static async updateUrl(tinyUrl: string, newLongUrl: string): Promise<string> {
    const destination = this.canonicalizeShareUrl(newLongUrl);
    try {
      const shortUrl = await this.postShorten({
        action: 'update',
        tinyUrl: tinyUrl,
        newLongUrl: destination,
      });
      console.log('[UrlShorteningService] Successfully updated URL:', shortUrl);
      return shortUrl;
    } catch (error) {
      console.error('[UrlShorteningService] URL update failed:', error);
      return newLongUrl;
    }
  }

  /**
   * Checks if the service is properly configured
   * @returns boolean Always returns true since configuration is server-side
   */
  static isConfigured(): boolean {
    return true;
  }
}
